import React, { useState } from 'react';
import styled from 'styled-components';
import Layout from '../components/Layout';
import { contactSchemas } from '../lib/schemas';

const HeroSection = styled.section`
  background-color: #7AA6A2;
  padding: 6rem 2rem 7rem;
  text-align: center;
  position: relative;
  z-index: 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 65%;
    height: 100%;
    background-image: url('/img/background.png');
    background-repeat: no-repeat;
    background-size: cover;
    -webkit-mask-image: linear-gradient(to right, transparent 0%, black 50%);
    mask-image: linear-gradient(to right, transparent 0%, black 50%);
    pointer-events: none;
    z-index: -1;
  }
`;

const HeroWave = styled.div`
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  line-height: 0;
  pointer-events: none;
  svg { width: 100%; height: 64px; display: block; }
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 3rem;
  font-weight: 700;

  @media (max-width: 600px) {
    font-size: 2.2rem;
  }
`;

/* ── Lead (below hero) ── */
const LeadSection = styled.section`
  background-color: #fff;
  padding: 4rem 2rem 4.5rem;
  text-align: center;
`;

const LeadText = styled.p`
  color: #555;
  font-size: 1.2rem;
  line-height: 1.8;
  max-width: 600px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  padding: 5rem 2rem;
  max-width: 900px;
  margin: 0 auto;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const SectionTitle = styled.h2`
  color: #333;
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
`;

const BodyText = styled.p`
  color: #555;
  font-size: 1.05rem;
  line-height: 1.75;
  margin-bottom: 1rem;
`;

const BenefitList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0 0;
`;

const BenefitItem = styled.li`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  margin-bottom: 1rem;
  color: #555;
  font-size: 1rem;
  line-height: 1.6;

  &::before {
    content: '✓';
    color: #7AA6A2;
    font-weight: 700;
    margin-top: 1px;
    flex-shrink: 0;
  }
`;

const FormCard = styled.div`
  background: #fff;
  border: 1px solid #e8e5e2;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.06);
`;

const FormTitle = styled.h3`
  color: #333;
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const NameRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #444;
  margin-bottom: 0.4rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.65rem 0.9rem;
  border: 1px solid #ccc;
  border-radius: 100px;
  font-size: 1rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #7AA6A2;
    box-shadow: 0 0 0 2px rgba(122,166,162,0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.65rem 0.9rem;
  border: 1px solid #ccc;
  border-radius: 100px;
  font-size: 1rem;
  background: #fff;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #7AA6A2;
    box-shadow: 0 0 0 2px rgba(122,166,162,0.2);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.65rem 0.9rem;
  border: 1px solid #ccc;
  border-radius: 12px;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #7AA6A2;
    box-shadow: 0 0 0 2px rgba(122,166,162,0.2);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: #7AA6A2;
  color: #fff;
  border: none;
  padding: 0.85rem 1.5rem;
  border-radius: 100px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 0.5rem;

  &:hover {
    background-color: #5a8a87;
  }
`;

const SuccessBox = styled.div`
  background-color: #f0f8f7;
  border: 1px solid #7AA6A2;
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  color: #3d7a75;
`;

const SuccessTitle = styled.h3`
  color: #3d7a75;
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
`;

const TOPICS = [
  'Our Books',
  'Speaking at Your Event',
  'Workshops & Training',
  'Other',
];

const HUBSPOT_PORTAL_ID = '244482894';
const HUBSPOT_FORM_ID = '65e30b2e-986b-4a70-a7e4-8e3f36161fce';

const FreeCoaching = () => {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', topic: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fields: [
              { name: 'firstname', value: form.firstName },
              { name: 'lastname',  value: form.lastName  },
              { name: 'email',     value: form.email     },
              { name: 'subject',   value: form.topic     },
              { name: 'message',   value: form.message   },
            ],
          }),
        }
      );
      setSubmitted(true);
    } catch (err) {
      console.error('HubSpot submission error:', err);
      setError(true);
    }
  };

  return (
    <Layout
      title="Contact Us"
      description="Get in touch with Building on Truth — whether you're interested in our books, booking a speaker, or bringing a workshop to your organization."
      path="/contact"
      keywords="contact Building on Truth, book a speaker, workshop inquiry, Christian AI workshop"
      jsonLd={contactSchemas()}
    >
      <HeroSection>
        <HeroTitle>Contact Us</HeroTitle>
        <HeroWave>
          <svg viewBox="0 0 1440 64" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,64 C360,0 1080,0 1440,64 L1440,64 L0,64 Z" fill="#fff" />
          </svg>
        </HeroWave>
      </HeroSection>

      <LeadSection>
        <LeadText>
          Whether you're interested in our books, want to bring us in to speak, or are looking for workshops and training — we'd love to hear from you.
        </LeadText>
      </LeadSection>

      <ContentSection>
        <TwoCol>
          <div>
            <SectionTitle>How can we help?</SectionTitle>
            <BodyText>
              We work with churches, conferences, universities, and organizations around the world. Drop us a note and we'll get back to you within a couple of business days.
            </BodyText>
            <BenefitList>
              <BenefitItem>Interested in our books on AI, faith, entrepreneurship, and discipleship</BenefitItem>
              <BenefitItem>Looking to book Building on Truth to speak at your event or conference</BenefitItem>
              <BenefitItem>Want to bring our workshops and training to your organization or church</BenefitItem>
            </BenefitList>
          </div>

          <FormCard>
            {submitted ? (
              <SuccessBox>
                <SuccessTitle>Message received!</SuccessTitle>
                <p style={{ margin: 0, color: '#3d7a75' }}>
                  We'll be in touch within 1–2 business days.
                </p>
              </SuccessBox>
            ) : (
              <>
                <FormTitle>Tell us a bit about yourself</FormTitle>
                <form onSubmit={handleSubmit}>
                  <FormGroup>
                    <NameRow>
                      <div>
                        <Label htmlFor="firstName">First name</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          type="text"
                          value={form.firstName}
                          onChange={handleChange}
                          required
                          placeholder="Jane"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last name</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          type="text"
                          value={form.lastName}
                          onChange={handleChange}
                          required
                          placeholder="Smith"
                        />
                      </div>
                    </NameRow>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="email">Email address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="jane@example.com"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="topic">What are you interested in?</Label>
                    <Select
                      id="topic"
                      name="topic"
                      value={form.topic}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select one…</option>
                      {TOPICS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="message">Tell us more (optional)</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Any context about your organization, event, or what you're looking for…"
                    />
                  </FormGroup>
                  <SubmitButton type="submit">Send Message →</SubmitButton>
                  {error && (
                    <p style={{ color: '#c0392b', fontSize: '0.9rem', marginTop: '0.75rem', textAlign: 'center' }}>
                      Something went wrong — please try again.
                    </p>
                  )}
                </form>
              </>
            )}
          </FormCard>
        </TwoCol>
      </ContentSection>
    </Layout>
  );
};

export default FreeCoaching;
